import React, { useState, useEffect, useRef, useCallback } from 'react';
import './Discovery.css';

function Discovery({ API_URL, userId }) {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [offset, setOffset] = useState(0);
  const feedRef = useRef(null);
  const isScrolling = useRef(false);
  const touchStartY = useRef(0);
  const touchEndY = useRef(0);

  const loadFeed = useCallback(async (loadMore = false) => {
    if (loading) return;
    
    setLoading(true);
    try {
      const currentOffset = loadMore ? offset : 0;
      const response = await fetch(
        `${API_URL}/api/discovery/feed?user_id=${userId}&limit=10&offset=${currentOffset}`
      );
      
      if (response.ok) {
        const data = await response.json();
        const newPosts = data.posts || [];
        
        if (loadMore) {
          setPosts(prev => [...prev, ...newPosts]);
        } else {
          setPosts(newPosts);
          setCurrentIndex(0);
        }
        
        setHasMore(data.has_more);
        setOffset(currentOffset + newPosts.length);
      }
    } catch (err) {
      console.error('Error loading feed:', err);
    } finally {
      setLoading(false);
    }
  }, [API_URL, userId, loading, offset]);

  useEffect(() => {
    if (userId) {
      loadFeed();
    }
  }, [userId, loadFeed]);

  const handleLike = async (postId, isLiked) => {
    try {
      const response = await fetch(`${API_URL}/api/discovery/feed/like`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId,
          post_id: postId,
          is_liked: isLiked
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setPosts(prev => prev.map(post => 
          post.id === postId 
            ? { ...post, is_liked: data.is_liked, likes_count: data.likes_count }
            : post
        ));
      }
    } catch (err) {
      console.error('Error liking post:', err);
    }
  };

  const handleScroll = useCallback((e) => {
    if (isScrolling.current) return;
    
    const container = feedRef.current;
    if (!container) return;
    
    const scrollTop = container.scrollTop;
    const scrollHeight = container.scrollHeight;
    const clientHeight = container.clientHeight;
    
    // Calculate which post should be visible based on scroll position
    const postHeight = clientHeight; // Each post takes full viewport
    const newIndex = Math.round(scrollTop / postHeight);
    
    if (newIndex !== currentIndex && newIndex >= 0 && newIndex < posts.length) {
      setCurrentIndex(newIndex);
    }
    
    // Load more when near bottom
    if (scrollTop + clientHeight >= scrollHeight - 100 && hasMore && !loading) {
      loadFeed(true);
    }
  }, [currentIndex, posts.length, hasMore, loading, loadFeed]);

  const handleWheel = useCallback((e) => {
    e.preventDefault();
    const container = feedRef.current;
    if (!container || isScrolling.current) return;
    
    isScrolling.current = true;
    const delta = e.deltaY;
    const postHeight = container.clientHeight;
    
    let targetIndex = currentIndex;
    if (delta > 0 && currentIndex < posts.length - 1) {
      targetIndex = currentIndex + 1;
    } else if (delta < 0 && currentIndex > 0) {
      targetIndex = currentIndex - 1;
    }
    
    if (targetIndex !== currentIndex) {
      container.scrollTo({
        top: targetIndex * postHeight,
        behavior: 'smooth'
      });
      setCurrentIndex(targetIndex);
    }
    
    setTimeout(() => {
      isScrolling.current = false;
    }, 500);
  }, [currentIndex, posts.length]);

  const handleTouchStart = (e) => {
    touchStartY.current = e.touches[0].clientY;
  };

  const handleTouchMove = (e) => {
    touchEndY.current = e.touches[0].clientY;
  };

  const handleTouchEnd = () => {
    if (!touchStartY.current || !touchEndY.current) return;
    
    const diff = touchStartY.current - touchEndY.current;
    const threshold = 50; // Minimum swipe distance
    
    if (Math.abs(diff) > threshold) {
      const container = feedRef.current;
      if (!container) return;
      
      const postHeight = container.clientHeight;
      let targetIndex = currentIndex;
      
      if (diff > 0 && currentIndex < posts.length - 1) {
        // Swipe up - next post
        targetIndex = currentIndex + 1;
      } else if (diff < 0 && currentIndex > 0) {
        // Swipe down - previous post
        targetIndex = currentIndex - 1;
      }
      
      if (targetIndex !== currentIndex) {
        container.scrollTo({
          top: targetIndex * postHeight,
          behavior: 'smooth'
        });
        setCurrentIndex(targetIndex);
      }
    }
    
    touchStartY.current = 0;
    touchEndY.current = 0;
  };

  const handleKeyDown = useCallback((e) => {
    // Only handle keyboard navigation when feed is focused or has posts
    if (posts.length === 0) return;
    
    const container = feedRef.current;
    if (!container) return;
    
    const postHeight = container.clientHeight;
    let targetIndex = currentIndex;
    
    switch (e.key) {
      case 'ArrowDown':
      case 'PageDown':
      case ' ': // Spacebar
        e.preventDefault();
        if (currentIndex < posts.length - 1) {
          targetIndex = currentIndex + 1;
        }
        break;
      case 'ArrowUp':
      case 'PageUp':
        e.preventDefault();
        if (currentIndex > 0) {
          targetIndex = currentIndex - 1;
        }
        break;
      case 'Home':
        e.preventDefault();
        targetIndex = 0;
        break;
      case 'End':
        e.preventDefault();
        targetIndex = posts.length - 1;
        break;
      default:
        return; // Don't prevent default for other keys
    }
    
    if (targetIndex !== currentIndex) {
      container.scrollTo({
        top: targetIndex * postHeight,
        behavior: 'smooth'
      });
      setCurrentIndex(targetIndex);
    }
  }, [currentIndex, posts.length]);

  // Add keyboard event listener
  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const renderPostContent = (post) => {
    const metadata = post.metadata || {};
    
    switch (post.post_type) {
      case 'music_like':
        return (
          <div className="post-content music-like">
            <div className="music-info">
              <h3>🎵 {metadata.artist_name || 'Artist'}</h3>
              {metadata.track_name && <p className="track-name">{metadata.track_name}</p>}
            </div>
            {post.content && <p className="post-text">{post.content}</p>}
          </div>
        );
      
      case 'concert_attended':
        return (
          <div className="post-content concert-attended">
            <div className="concert-info">
              <h3>🎸 {metadata.show_name || 'Concert'}</h3>
              {metadata.venue_name && <p className="venue-name">{metadata.venue_name}</p>}
              {metadata.event_date && (
                <p className="event-date">{new Date(metadata.event_date).toLocaleDateString()}</p>
              )}
            </div>
            {post.content && <p className="post-text">{post.content}</p>}
          </div>
        );
      
      case 'tour_announcement':
        return (
          <div className="post-content tour-announcement">
            <div className="tour-info">
              <h3>🚀 Tour Announcement</h3>
              {metadata.tour_name && <p className="tour-name">{metadata.tour_name}</p>}
              {metadata.cities && (
                <p className="tour-cities">{metadata.cities.join(', ')}</p>
              )}
            </div>
            {post.content && <p className="post-text">{post.content}</p>}
          </div>
        );
      
      case 'music_recommendation':
        return (
          <div className="post-content music-recommendation">
            <div className="recommendation-info">
              <h3>💡 Recommendation</h3>
              {metadata.artist_name && <p className="artist-name">{metadata.artist_name}</p>}
              {metadata.reason && <p className="reason">{metadata.reason}</p>}
            </div>
            {post.content && <p className="post-text">{post.content}</p>}
          </div>
        );
      
      default:
        return post.content ? <p className="post-text">{post.content}</p> : null;
    }
  };

  const seedSamplePosts = async () => {
    try {
      const response = await fetch(`${API_URL}/api/discovery/feed/seed`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('Seed result:', data);
        // Reload feed after seeding
        loadFeed();
      }
    } catch (err) {
      console.error('Error seeding posts:', err);
    }
  };

  if (!userId) {
    return (
      <div className="discovery-feed">
        <div className="empty-state">
          <p>Please sign in to see your social feed</p>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="discovery-feed" 
      ref={feedRef} 
      onScroll={handleScroll} 
      onWheel={handleWheel}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      tabIndex={0}
      role="feed"
      aria-label="Social feed"
    >
      {posts.length === 0 && !loading ? (
        <div className="empty-state">
          <p>No posts yet. Connect with friends or follow artists to see content!</p>
          <button 
            onClick={seedSamplePosts} 
            className="btn-primary" 
            style={{ marginTop: '1rem', minHeight: '44px', minWidth: '200px' }}
            type="button"
          >
            Load Sample Posts
          </button>
          <p style={{ marginTop: '1rem', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
            Tip: Use arrow keys or swipe to navigate posts
          </p>
        </div>
      ) : (
        <div className="posts-container">
          {posts.map((post, index) => (
            <div key={post.id} className={`post-card ${index === currentIndex ? 'active' : ''}`}>
              {post.image_url && (
                <div className="post-image">
                  <img src={post.image_url} alt="Post" />
                </div>
              )}
              
              <div className="post-body">
                <div className="post-header">
                  <div className="author-info">
                    {post.author?.avatar_url && (
                      <img src={post.author.avatar_url} alt={post.author.name} className="author-avatar" />
                    )}
                    <div className="author-details">
                      <div className="author-name">
                        {post.author?.name || 'Unknown'}
                        {post.author?.is_verified && <span className="verified-badge">✓</span>}
                        {post.is_friend && <span className="friend-badge">Friend</span>}
                      </div>
                      {post.author?.username && (
                        <div className="author-username">@{post.author.username}</div>
                      )}
                    </div>
                  </div>
                  <div className="post-time">{formatTimeAgo(post.created_at)}</div>
                </div>
                
                {renderPostContent(post)}
                
                <div className="post-actions">
                  <button
                    className={`action-btn like-btn ${post.is_liked ? 'liked' : ''}`}
                    onClick={() => handleLike(post.id, post.is_liked)}
                    aria-label={`Like post by ${post.author?.name || 'Unknown'}. ${post.likes_count || 0} likes`}
                    type="button"
                  >
                    <span className="icon" aria-hidden="true">❤️</span>
                    <span className="count">{post.likes_count || 0}</span>
                  </button>
                  <button 
                    className="action-btn comment-btn"
                    aria-label={`Comment on post. ${post.comments_count || 0} comments`}
                    type="button"
                  >
                    <span className="icon" aria-hidden="true">💬</span>
                    <span className="count">{post.comments_count || 0}</span>
                  </button>
                  <button 
                    className="action-btn share-btn"
                    aria-label="Share post"
                    type="button"
                  >
                    <span className="icon" aria-hidden="true">🔗</span>
                  </button>
                </div>
              </div>
            </div>
          ))}
          
          {loading && (
            <div className="loading-post">
              <p>Loading more posts...</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default Discovery;
